import React, { useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Search, Shield, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function BookingLookup() {
  const [step, setStep] = useState("choose"); // choose | otp-mobile | otp-verify | id | results
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [demoOtp, setDemoOtp] = useState(null);
  const [bookingId, setBookingId] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const requestOtp = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const r = await api.post("/bookings/request-otp", { mobile });
      setDemoOtp(r.data.demo_otp);
      setStep("otp-verify");
      toast.success("OTP sent to your WhatsApp/SMS");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send OTP"); }
    setLoading(false);
  };
  const verifyOtp = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const r = await api.post("/bookings/verify-otp", { mobile, otp });
      setItems(r.data.bookings || []);
      setStep("results");
      if ((r.data.bookings||[]).length === 0) toast.info("No bookings under this mobile.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Verification failed"); }
    setLoading(false);
  };
  const searchById = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const r = await api.post("/bookings/lookup", { booking_id: bookingId });
      setItems(r.data || []); setStep("results");
    } catch { setItems([]); setStep("results"); }
    setLoading(false);
  };
  const reset = () => { setStep("choose"); setMobile(""); setOtp(""); setDemoOtp(null); setBookingId(""); setItems([]); };

  return (
    <PublicLayout>
      <section className="max-w-2xl mx-auto px-4 pt-10 pb-16">
        <div className="text-xs uppercase tracking-widest text-[#ea580c] font-semibold">Track Booking</div>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-display font-black tracking-tight">Find your booking</h1>
        <p className="text-slate-600 mt-3 text-sm">Verify via OTP for passenger privacy, or look up by Booking ID directly.</p>

        {step === "choose" && (
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <button onClick={()=>setStep("otp-mobile")} className="p-6 rounded-2xl bg-white border border-slate-200 hover:border-[#ea580c] text-left transition-colors" data-testid="lookup-choice-otp">
              <Shield className="w-8 h-8 text-[#ea580c]" />
              <div className="font-display font-bold text-lg mt-3">By Mobile + OTP</div>
              <div className="text-sm text-slate-500 mt-1">See all your bookings safely — we send a 6-digit code to your number.</div>
            </button>
            <button onClick={()=>setStep("id")} className="p-6 rounded-2xl bg-white border border-slate-200 hover:border-[#ea580c] text-left transition-colors" data-testid="lookup-choice-id">
              <Search className="w-8 h-8 text-[#ea580c]" />
              <div className="font-display font-bold text-lg mt-3">By Booking ID</div>
              <div className="text-sm text-slate-500 mt-1">Enter your TRV-… ID to jump directly to a booking.</div>
            </button>
          </div>
        )}

        {step === "otp-mobile" && (
          <form onSubmit={requestOtp} className="mt-6 p-6 rounded-2xl bg-white border border-slate-200 space-y-4" data-testid="otp-mobile-form">
            <div><Label>Mobile Number</Label><Input required value={mobile} onChange={e=>setMobile(e.target.value)} placeholder="+91…" data-testid="otp-mobile-input" /></div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={reset} data-testid="otp-back">Back</Button>
              <Button disabled={loading} type="submit" className="flex-1 bg-[#ea580c] hover:bg-orange-700" data-testid="otp-send">{loading?"Sending…":"Send OTP"}</Button>
            </div>
          </form>
        )}

        {step === "otp-verify" && (
          <form onSubmit={verifyOtp} className="mt-6 p-6 rounded-2xl bg-white border border-slate-200 space-y-4" data-testid="otp-verify-form">
            <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm flex items-start gap-2">
              <MessageCircle className="w-4 h-4 text-blue-600 mt-0.5" />
              <div>
                <div className="font-semibold text-blue-900">OTP sent to {mobile}</div>
                {demoOtp && <div className="text-blue-700 mt-1">Demo mode — your OTP is <b>{demoOtp}</b> (in production this comes via WhatsApp/SMS).</div>}
              </div>
            </div>
            <div><Label>Enter 6-digit OTP</Label><Input required value={otp} onChange={e=>setOtp(e.target.value)} maxLength={6} placeholder="123456" data-testid="otp-code-input" /></div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={()=>setStep("otp-mobile")} data-testid="otp-verify-back">Back</Button>
              <Button disabled={loading} type="submit" className="flex-1 bg-[#ea580c] hover:bg-orange-700" data-testid="otp-verify-submit">{loading?"Verifying…":"Verify & View"}</Button>
            </div>
          </form>
        )}

        {step === "id" && (
          <form onSubmit={searchById} className="mt-6 p-6 rounded-2xl bg-white border border-slate-200 space-y-4" data-testid="lookup-id-form">
            <div><Label>Booking ID</Label><Input required value={bookingId} onChange={e=>setBookingId(e.target.value)} placeholder="TRV-…" data-testid="lookup-id-input" /></div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={reset} data-testid="lookup-id-back">Back</Button>
              <Button disabled={loading} type="submit" className="flex-1 bg-[#ea580c] hover:bg-orange-700" data-testid="lookup-id-submit">{loading?"Searching…":"Find"}</Button>
            </div>
          </form>
        )}

        {step === "results" && (
          <div className="mt-6 space-y-3">
            <Button variant="outline" onClick={reset} data-testid="lookup-reset">← Search again</Button>
            {items.length===0 && <div className="p-6 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900">No booking found.</div>}
            {items.map(b => (
              <Link to={`/book/confirm/${b.booking_id}`} key={b.booking_id} className="block p-5 rounded-xl bg-white border border-slate-200 hover:border-[#ea580c] transition-colors" data-testid={`lookup-result-${b.booking_id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display font-bold text-lg text-[#1e1b4b] truncate">{b.from_place} → {b.to_place}</div>
                    <div className="text-xs text-slate-500 mt-1 truncate">{b.booking_id} · {b.travel_date} · {b.passengers} pax</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-xs px-2 py-0.5 rounded-full bg-slate-100 capitalize inline-block">{b.status.replace(/_/g,' ')}</div>
                    <div className="text-lg font-black text-[#ea580c] mt-1">₹{b.fare.toLocaleString()}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
